import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  AcceptInviteInput,
  AcceptInviteOutput,
  AddMemberInput,
  BackfillPersonalOrganizationsOutput,
  CreateOrganizationInput,
  CreatePersonalOrganizationInput,
  DeclineInviteInput,
  GetOrganizationInput,
  InvitationIdInput,
  InvitationLinkOutput,
  InvitationsOutput,
  InviteMemberInput,
  ListOrganizationsInput,
  ListOrganizationsOutput,
  OrganizationPage,
  PageOrganizationsInput,
  MembersOutput,
  Membership,
  MembershipsForUserInput,
  MintedInviteLink,
  MintInviteLinkInput,
  MyOrganizationsOutput,
  Ok,
  Organization,
  OrganizationIdsForUsersInput,
  OrganizationIdsForUsersOutput,
  OrganizationMembersInput,
  OrganizationMembersOutput,
  RemoveMemberInput,
  RenameOrganizationInput,
  RequestOrganizationDeletionInput,
  RestoreOrganizationInput,
  SetMemberDisabledInput,
  SetMemberRoleInput,
  SetOrganizationLogoInput,
} from "./organization.schemas.ts";

// The promise organization makes to everyone else. It is the tenant
// (ADR-001 §1): every module that moves to organization_id (S1.03) reaches
// it only through this token, never by importing its tables.

/** Who opens the organization; the author of the row, not necessarily a member. */
export type CreatedBy = { readonly createdBy: string };

/** Who asked for a role change, so the service can refuse self-promotion even
 * if a caller ever reached it without the permission door's own check (S3.02). */
export type ActorUserId = { readonly actorUserId: string };

/** Who is asking to see the directory, so it can answer their own role (S3.02). */
export type CallerUserId = { readonly callerUserId: string };

/** The switcher's own self-service call (S1.05): `activeOrganizationId` is whatever
 * the request already resolved (S4.03), echoed back so it can mark the current one
 * without a second round trip. */
export type MyOrganizationsRequest = {
  readonly userId: string;
  readonly activeOrganizationId: string | undefined;
};

export interface OrganizationApi extends EraseStep {
  create(input: CreateOrganizationInput & CreatedBy): Promise<Organization>;
  /** Sign-up's hook (S1.02): one organization named after the person, who becomes its only
   * admin. Peer-only; auth calls it from `signUp`, never on the wire. */
  createPersonal(input: CreatePersonalOrganizationInput & CreatedBy): Promise<Organization>;
  get(input: GetOrganizationInput): Promise<Organization>;
  rename(input: RenameOrganizationInput): Promise<Organization>;
  /** Sets or clears the logo (S1.06); `dataUrl: null` clears it, otherwise checked and
   * decoded by `@amtana/media` first. */
  setLogo(input: SetOrganizationLogoInput): Promise<Organization>;
  /** Starts the grace period (S1.06); refused outright for an organization already
   * inside one. Peer-only since D307: no customer may delete an organization any
   * more (every member refused, admin included); auth's own account-deletion
   * cascade (S6.10) is the only caller, for the one organization this person is
   * the sole member of. */
  requestDeletion(input: RequestOrganizationDeletionInput): Promise<Organization>;
  /** Undoes a pending deletion inside its grace period; checked against the caller's
   * own admin membership directly, since a pending-deletion organization carries no
   * live permissions for the usual door to gate on (S1.06). */
  restoreOrganization(input: RestoreOrganizationInput & { readonly userId: string }): Promise<Organization>;
  myOrganizations(input: MyOrganizationsRequest): Promise<MyOrganizationsOutput>;
  /** Adds a person to an organization with a role (S3.01); idempotent, so adding someone
   * already a member answers their existing membership rather than refusing. */
  addMember(input: AddMemberInput): Promise<Membership>;
  /** Disables a member without removing their row, so their history stays (S3.01); refused
   * when they are the organization's only active admin (S3.03). */
  setMemberDisabled(input: SetMemberDisabledInput): Promise<Membership>;
  /** Every organization a person belongs to and their role in each; the seam S1.03 (scoping
   * a request to an organization) and S4.03 (enforcing a role) read from. Peer-only. */
  membershipsForUser(input: MembershipsForUserInput): Promise<readonly Membership[]>;
  /** The directory (S3.02): every member of the caller's organization, with their name
   * and email, and the caller's own role so the screen knows what to offer. */
  members(input: { readonly organizationId: string } & CallerUserId): Promise<MembersOutput>;
  /** Changes a member's role (S3.02); refuses a self-promotion (defence in depth) and
   * refuses demoting the organization's only active admin (S3.03). */
  setMemberRole(input: SetMemberRoleInput & ActorUserId): Promise<Membership>;
  /** Removes a member outright (S3.02); refuses removing the organization's only
   * active admin (S3.03). */
  removeMember(input: RemoveMemberInput): Promise<Ok>;
  /** A member leaves on their own (S3.02); refused for the organization's only active
   * admin (S3.03) and for a person's only organization (every person needs one). */
  leave(input: { readonly organizationId: string; readonly userId: string }): Promise<Ok>;
  /** One-time, idempotent: every account with no membership yet gets a personal organization
   * (S1.02's backfill for accounts that existed before this story). Safe to run more than once.
   * Peer-only: no customer may run this, so it carries no tRPC declaration; an operator calls
   * it from `apps/api/scripts/backfill-personal-organizations.mjs` (D036). */
  backfillPersonalOrganizations(): Promise<BackfillPersonalOrganizationsOutput>;
  /** Lets held invitations out once they fit the organization's current seat cap (S3.07);
   * a scheduled sweep already calls this periodically. Peer-only: no customer runs it
   * directly, so it carries no tRPC declaration. */
  releaseHeldInvites(): Promise<number>;
  /** Billing pushes the seat cap its own plan and purchased extra seats add up to
   * (S3.07, S5.12), whenever either changes; billing has no peer back to this module
   * (it would close a cycle), so this is how it travels. Peer-only. */
  setSeatAllowance(input: {
    readonly organizationId: string;
    readonly maxSeats: number | null;
  }): Promise<void>;
  /** How many active (non-disabled) members belong to the organization right now
   * (S5.12); billing reads this to price seats, since it peers with this module. Peer-only. */
  activeMemberCount(input: { readonly organizationId: string }): Promise<number>;
  /** Every active admin of the organization (S7.08): a peer notifying the whole admin
   * roster, such as a usage warning or a failed recharge, has no caller membership of
   * its own to check against, unlike `members`. Peer-only. */
  adminUserIds(input: { readonly organizationId: string }): Promise<readonly string[]>;
  /** Every named user's active (earliest-joined) organization id, for a module's own
   * backfill (S1.03, ADR-001 §5) to stamp onto rows that only ever carried an owner.
   * Peer-only: no customer's row is looked up by someone else's request. */
  organizationIdsForUsers(input: OrganizationIdsForUsersInput): Promise<OrganizationIdsForUsersOutput>;
  /** Invites an address to join the caller's organization with a role (S3.04); refused
   * outright when that address already belongs to it. Mails the link and answers it once,
   * since only its hash is ever stored. */
  inviteMember(
    input: InviteMemberInput & { readonly invitedByUserId: string },
  ): Promise<InvitationLinkOutput>;
  /** Every pending invitation of the caller's organization (S3.06). */
  invitations(input: { readonly organizationId: string }): Promise<InvitationsOutput>;
  /** Mints a fresh link, invalidating whatever was out before, and mails it again;
   * rate limited per invitation (S3.06). */
  resendInvite(input: InvitationIdInput): Promise<InvitationLinkOutput>;
  /** Mints a fresh link the same way `resendInvite` does, without mailing it, for an
   * admin who wants to copy it by hand (S3.06). */
  copyInviteLink(input: InvitationIdInput): Promise<InvitationLinkOutput>;
  /** Kills an invitation's link outright (S3.06). */
  revokeInvite(input: InvitationIdInput): Promise<Ok>;
  /** Mints a fresh, single-use link for the invitation named, replacing whatever token it
   * already held (the newest link wins), and never stores or logs it in the clear (ADR-022
   * point 7, S26.13). Peer-only, reached through worker-bridge the moment a `transactionalEmail`
   * delivery is about to mail it, never notification directly (a cycle through organization's
   * own peer on it before this story, D500's own shape). */
  mintInviteLink(input: MintInviteLinkInput): Promise<MintedInviteLink>;
  /** Spends an invite's link: adds the signed-in caller as a member with the invited
   * role, once their verified address matches it (S3.05). Idempotent on a repeat call
   * from whoever already accepted it. */
  acceptInvite(input: AcceptInviteInput & { readonly userId: string }): Promise<AcceptInviteOutput>;
  /** Kills an invitation's link the way revoking does, from the invited person's own
   * side (S3.06); needs no session, since it proves nothing and creates nothing. */
  declineInvite(input: DeclineInviteInput): Promise<Ok>;
  /** Whether the organization has room for one more seat right now (S1.07, S3.08):
   * active members plus already-mailed pending invitations against the cached
   * seat cap, the same count `inviteMember` holds an invite against (S3.07).
   * Peer-only; a caller that needs to add a member outside the invite flow
   * (domain join, an approved access request) checks this first, the same way
   * an invite is held rather than refused when the organization is full. */
  hasSeatCapacity(input: { readonly organizationId: string }): Promise<boolean>;
  /** The back office's own directory (S9.03, ADR-005 point 4): every organization, optionally
   * filtered by a name or slug substring; state only, this module's own facts, never a
   * customer's content. Peer-only. */
  listOrganizations(input: ListOrganizationsInput): Promise<ListOrganizationsOutput>;
  /** The same directory one keyset page at a time (ADR-033), sorted and narrowed by this module
   * over its own tables, optionally inside an id selection a peer computed. Peer-only. */
  pageOrganizations(input: PageOrganizationsInput): Promise<OrganizationPage>;
  /** One organization's members with their roles (S9.03); unlike `members`, the caller need
   * not belong to it. Peer-only. */
  organizationMembers(input: OrganizationMembersInput): Promise<OrganizationMembersOutput>;
  /** Every organization whose own deletion grace period has elapsed, for a peer's own
   * organization-scoped purge sweep (ADR-026, epic 30, S30.07) to run ahead of
   * `organization-purge.task.ts`'s own hourly sweep, which drops the row outright: a module
   * that waited to hear it from organization directly would find nothing left to key its own
   * purge on. Peer-only; mirrors `AccountApi.accountsPastGracePeriod` (S30.01). */
  organizationsPastGracePeriod(input: { readonly now: string }): Promise<readonly string[]>;
  /** Every organization this person is the only member of, in any role (review group 3
   * security finding 2): the one computation auth's own erasure gate, and `/ops`'s own person
   * erasure (preview and erase alike), each used to loop a membership roster and an
   * `organizationMembers` read for themselves — three copies of the identical decision, now
   * one. An empty roster counts as sole too (D875): a person's own sole organization that
   * organization's own erase step (or the ordinary grace-period purge sweep) has already
   * purged can never come back with a member in it, so finding none there still means "this
   * was the person's own organization alone," ordering-independent of whichever erase-step
   * intent lands first. Peer-only. */
  soleOrganizationsOf(input: { readonly userId: string }): Promise<readonly string[]>;
}

export const OrganizationApi = moduleApi<OrganizationApi>()("organization");
